// src/pages/AdminDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { connectSocket, disconnectSocket } from '../services/socket';
import useAuthStore from '../store/authStore';
import './Admin.css';

const getLatestRemark = (remarks) => {
  if (!remarks || !Array.isArray(remarks) || remarks.length === 0) return '';
  return remarks[remarks.length - 1]?.text || '';
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, loading } = useAuth();
  
  // Helper function to get correct role name
  const getUserRole = () => {
    if (!user) return 'User';
    
    if (typeof user.role === 'object' && user.role !== null) {
      return user.role.name || 'User';
    }
    
    if (typeof user.role === 'string') {
      return user.role;
    }
    
    return 'User';
  };

  const userRole = getUserRole();
  const isSuperAdmin = userRole.toLowerCase() === 'superadmin';
  const isHR = userRole.toLowerCase() === 'hr';
  const isPrivileged = isSuperAdmin || isHR;
  
  // State for UI
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // State for data
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalContacts: 0,
    pendingContacts: 0
  });
  
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [myCurrentPage, setMyCurrentPage] = useState(1);
  const [myTotalPages, setMyTotalPages] = useState(1);
  const [myItemsPerPage, setMyItemsPerPage] = useState(10);
  const [contactStats, setContactStats] = useState({ new: 0, assigned: 0, in_progress: 0, followup: 0, won: 0, lost: 0, total: 0 });
  const [myContactStats, setMyContactStats] = useState({ new: 0, assigned: 0, in_progress: 0, followup: 0, won: 0, lost: 0, total: 0 });
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [selectedItem, setSelectedItem] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Edit Contact Modal state
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  // Add / Edit User Modal state
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({ name: '', email: '', phone: '', password: '', role: '' });

  // Add Role Modal state
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');

  // My Contacts (for regular users)
  const [myContacts, setMyContacts] = useState([]);
  const [myContactsLoading, setMyContactsLoading] = useState(false);

  // Assign Contact Modal
  const [showAssignContactModal, setShowAssignContactModal] = useState(false);
  const [assigningContactId, setAssigningContactId] = useState('');
  const [assignContactUserId, setAssignContactUserId] = useState('');

  // Status filter for contacts table (for privileged) and my-contacts table (for BDE)
  const [statusFilter, setStatusFilter] = useState('');
  const [myStatusFilter, setMyStatusFilter] = useState('');

  // Attendance state
  const [todayAttendance, setTodayAttendance] = useState(undefined); // undefined = not loaded yet
  const [myAttendance, setMyAttendance] = useState([]);
  const [myAttendancePage, setMyAttendancePage] = useState(1);
  const [myAttendanceTotalPages, setMyAttendanceTotalPages] = useState(1);
  const [myAttendanceStats, setMyAttendanceStats] = useState({ present: 0, absent: 0, half_day: 0, late: 0, leave: 0, total: 0 });
  const [allAttendance, setAllAttendance] = useState([]);
  const [allAttendancePage, setAllAttendancePage] = useState(1);
  const [allAttendanceTotalPages, setAllAttendanceTotalPages] = useState(1);
  const [attendanceDateFilter, setAttendanceDateFilter] = useState('');
  const [attendanceUserFilter, setAttendanceUserFilter] = useState('');
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [checkInOutLoading, setCheckInOutLoading] = useState(false);
  const [showManualMarkModal, setShowManualMarkModal] = useState(false);
  const [manualMarkForm, setManualMarkForm] = useState({ userId: '', date: '', status: 'absent', notes: '' });
  const [showCheckoutConfirm, setShowCheckoutConfirm] = useState(false);
  const [lunchLoading, setLunchLoading] = useState(false);
  const [shortBreakLoading, setShortBreakLoading] = useState(false);

  // Persistent notification bell
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const notifPanelRef = useRef(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch initial supporting data (stats, roles, users — contacts handled by consolidated effects)
  useEffect(() => {
    if (isAuthenticated) {
      if (isPrivileged) {
        fetchStats();
        fetchRoles();
        fetchUsers();
      } else {
        fetchMyStats();
      }
    }
  }, [isAuthenticated, user]); // eslint-disable-line

  // Reset page and re-fetch non-contact data on tab change
  useEffect(() => {
    if (!isAuthenticated) return;
    setCurrentPage(1);
    setMyCurrentPage(1);
    if (isPrivileged) {
      if (activeTab === 'users' || activeTab === 'roles') fetchUsers();
      if (activeTab === 'dashboard') fetchStats();
    }
    if (activeTab === 'attendance') {
      fetchTodayAttendance();
      fetchMyAttendanceStats();
      fetchMyAttendanceHistory(1);
      if (isPrivileged) fetchAllAttendance(1);
    }
    // contact/mycontacts data re-fetched automatically by the consolidated effect below
  }, [activeTab]); // eslint-disable-line

  // Socket.IO real-time notifications
  useEffect(() => {
    if (!isAuthenticated) return;

    const token = useAuthStore.getState().token;
    if (!token) return;

    const socket = connectSocket(token);

    if (isPrivileged) {
      socket.on("new_contact", (payload) => {
        addNotification('new_contact', payload.message, payload.data);
        fetchContacts();
        fetchStats();
      });
      socket.on("status_changed", (payload) => {
        addNotification('status_changed', payload.message, payload.data);
        fetchContacts();
        fetchStats();
      });
      socket.on("note_added", (payload) => {
        addNotification('note_added', payload.message, payload.data);
        fetchContacts();
      });
    } else {
      socket.on("contact_assigned", (payload) => {
        addNotification('contact_assigned', payload.message, payload.data);
        fetchMyContacts();
        fetchMyStats();
      });
      socket.on("status_changed", (payload) => {
        addNotification('status_changed', payload.message, payload.data);
        fetchMyContacts();
        fetchMyStats();
      });
      socket.on("note_added", (payload) => {
        addNotification('note_added', payload.message, payload.data);
        fetchMyContacts();
      });
    }

    return () => {
      socket.off("new_contact");
      socket.off("status_changed");
      socket.off("note_added");
      socket.off("contact_assigned");
    };
  }, [isAuthenticated, isPrivileged]);

  // Disconnect socket on logout
  useEffect(() => {
    if (!isAuthenticated) disconnectSocket();
  }, [isAuthenticated]);

  // Close notification panel when clicking outside
  useEffect(() => {
    if (!showNotifPanel) return;
    const handleOutside = (e) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) {
        setShowNotifPanel(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showNotifPanel]);

  // Re-fetch contacts whenever page/perPage/search/status changes (privileged)
  useEffect(() => {
    if (!isAuthenticated || !isPrivileged) return;
    fetchContacts({ page: currentPage, limit: itemsPerPage, search: searchTerm, status: statusFilter });
  }, [currentPage, itemsPerPage, searchTerm, statusFilter]); // eslint-disable-line

  // Re-fetch my contacts whenever page/perPage/status changes (BDE)
  useEffect(() => {
    if (!isAuthenticated || isPrivileged) return;
    fetchMyContacts({ page: myCurrentPage, limit: myItemsPerPage, status: myStatusFilter });
  }, [myCurrentPage, myItemsPerPage, myStatusFilter]); // eslint-disable-line

  // FETCH STATS — uses lightweight aggregate endpoint, no full contact scan
  const fetchStats = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        api.get('/contacts/stats'),
        isSuperAdmin ? api.get('/users') : Promise.resolve(null)
      ]);

      const cs = statsRes.data?.data || {};
      setContactStats(cs);
      setStats({
        totalUsers: usersRes ? (usersRes.data?.length || Array.isArray(usersRes.data) ? usersRes.data.length : 0) : 0,
        totalContacts: cs.total || 0,
        pendingContacts: cs.new || 0
      });
    } catch (error) {}
  };

  const fetchMyStats = async () => {
    try {
      const res = await api.get('/contacts/my/stats');
      setMyContactStats(res.data?.data || {});
    } catch {}
  };

  // CSV EXPORT FUNCTIONS
  const convertToCSV = (data, columns) => {
    if (!data || data.length === 0) return '';
    const header = columns.map(col => `"${col.header}"`).join(',');
    const rows = data.map((item, index) => {
      return columns.map(col => {
        let value = '';
        if (typeof col.accessor === 'function') {
          value = col.accessor(item, index);
        } else {
          value = item[col.accessor] || '';
        }
        value = String(value).replace(/"/g, '""');
        return `"${value}"`;
      }).join(',');
    });
    return [header, ...rows].join('\n');
  };

  const downloadCSV = (csvContent, filename) => {
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.click();
    URL.revokeObjectURL(url);
  };

  // EXPORT ALL USERS TO CSV
  const exportUsersToCSV = async () => {
    if (!isSuperAdmin) {
      showNotification('❌ Access denied', 'error');
      return;
    }
    try {
      setExportingCSV(true);
      showNotification('📥 Fetching all users data...', 'success');
      const response = await api.get('/users', { params: { limit: 999999 } });
      let usersData = response.data?.data || response.data || [];
      if (usersData.length === 0) {
        showNotification('❌ No users data to export', 'error');
        return;
      }
      const columns = [
        { header: 'S.No', accessor: (item, index) => index + 1 },
        { header: 'Email', accessor: 'email' },
        { header: 'Role', accessor: (item) => getRoleName(item.role) },
        { header: 'Status', accessor: () => 'Active' }
      ];
      const csvContent = convertToCSV(usersData, columns);
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      downloadCSV(csvContent, `users_export_${timestamp}.csv`);
      showNotification(`✅ Successfully exported ${usersData.length} users!`, 'success');
    } catch (error) {
      showNotification('❌ Error exporting users data', 'error');
    } finally {
      setExportingCSV(false);
    }
  };

  // EXPORT ALL CONTACTS TO CSV
  const exportContactsToCSV = async () => {
    if (!isPrivileged) {
      showNotification('❌ Access denied', 'error');
      return;
    }
    try {
      setExportingCSV(true);
      showNotification('📥 Fetching all contacts data...', 'success');
      const response = await api.get('/contacts', { params: { limit: 999999 } });
      let contactsData = response.data?.data || response.data || [];
      if (contactsData.length === 0) {
        showNotification('❌ No contacts data to export', 'error');
        return;
      }
      const columns = [
        { header: 'S.No', accessor: (item, index) => index + 1 },
        { header: 'Name', accessor: 'name' },
        { header: 'Email', accessor: 'email' },
        { header: 'Phone', accessor: 'phone' },
        { header: 'Service', accessor: (item) => item.service || item.businessType || 'N/A' },
        { header: 'Message', accessor: (item) => item.message || item.requirements || 'N/A' },
        { header: 'Remarks', accessor: (item) => getLatestRemark(item.remarks) },
        { header: 'Date', accessor: (item) => item.createdAt ? new Date(item.createdAt).toLocaleDateString() : 'N/A' }
      ];
      const csvContent = convertToCSV(contactsData, columns);
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      downloadCSV(csvContent, `contacts_export_${timestamp}.csv`);
      showNotification(`✅ Successfully exported ${contactsData.length} contacts!`, 'success');
    } catch (error) {
      showNotification('❌ Error exporting contacts data', 'error');
    } finally {
      setExportingCSV(false);
    }
  };

  const fetchMyContacts = async (opts = {}) => {
    try {
      setMyContactsLoading(true);
      const page   = opts.page   !== undefined ? opts.page   : myCurrentPage;
      const limit  = opts.limit  !== undefined ? opts.limit  : myItemsPerPage;
      const status = opts.status !== undefined ? opts.status : myStatusFilter;

      const response = await api.get('/contacts/my', {
        params: { page, limit, status: status || undefined }
      });
      const { data = [], totalPages: tp = 1 } = response.data;
      setMyContacts(data);
      setMyTotalPages(tp);
    } catch (error) {
      setMyContacts([]);
    } finally {
      setMyContactsLoading(false);
    }
  };

  const fetchRoles = async () => {
    if (!isPrivileged) return;
    try {
      const response = await api.get('/roles');
      let rolesData = response.data?.data || response.data || [];
      setRoles(rolesData);
    } catch (error) {
      setRoles([]);
    }
  };

  const fetchUsers = async () => {
    if (!isPrivileged) return;
    try {
      setLoadingData(true);
      const response = await api.get('/users', {
        params: { page: currentPage, limit: itemsPerPage, search: searchTerm || undefined }
      });
      let usersData = response.data?.data || response.data || [];
      let total = response.data?.totalPages || response.data?.pagination?.totalPages || 1;
      setUsers(usersData);
      setTotalPages(total);
    } catch (error) {
      setUsers([]);
    } finally {
      setLoadingData(false);
    }
  };

  const fetchContacts = async (opts = {}) => {
    try {
      setLoadingData(true);
      const page   = opts.page   !== undefined ? opts.page   : currentPage;
      const limit  = opts.limit  !== undefined ? opts.limit  : itemsPerPage;
      const search = opts.search !== undefined ? opts.search : searchTerm;
      const status = opts.status !== undefined ? opts.status : statusFilter;

      const response = await api.get('/contacts', {
        params: {
          page,
          limit,
          search: search || undefined,
          status: status || undefined
        }
      });
      const { data = [], totalPages: tp = 1 } = response.data;
      setContacts(data);
      setFilteredContacts(data);
      setTotalPages(tp);
    } catch (error) {
      setContacts([]);
      setFilteredContacts([]);
    } finally {
      setLoadingData(false);
    }
  };

  const getRoleName = (roleId) => {
    if (!roleId) return 'User';
    if (typeof roleId === 'object' && roleId !== null && roleId.name) {
      return roleId.name;
    }
    if (typeof roleId === 'string') {
      return roleId.charAt(0).toUpperCase() + roleId.slice(1);
    }
    const foundRole = roles.find(r => r._id === roleId);
    return foundRole ? foundRole.name : 'User';
  };

  const getDisplayRoleName = () => {
    if (!user) return 'User';
    if (typeof user.role === 'object' && user.role !== null && user.role.name) {
      return user.role.name;
    }
    if (typeof user.role === 'string') {
      return user.role.charAt(0).toUpperCase() + user.role.slice(1);
    }
    return 'User';
  };

  const getRoleBadgeClass = (roleId) => {
    const roleName = getRoleName(roleId).toLowerCase();
    const roleMap = {
      'superadmin': 'superadmin',
      'admin': 'admin',
      'hr': 'hr',
      'bde': 'hr',
      'user': 'user'
    };
    return roleMap[roleName] || 'user';
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setEditFormData({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      service: contact.service || contact.businessType || '',
      message: contact.message || contact.requirements || '',
      // remarks managed separately via addRemark endpoint
    });
    setShowEditContactModal(true);
  };

  const handleUpdateContact = async (e) => {
    e.preventDefault();
    if (!editingContact?._id) {
      showNotification('❌ No contact selected for update', 'error');
      return;
    }
    try {
      setLoadingData(true);
      await api.put(`/contacts/${editingContact._id}`, editFormData);
      showNotification('✅ Contact updated successfully!', 'success');
      setShowEditContactModal(false);
      setEditingContact(null);
      await fetchContacts();
      fetchStats();
    } catch (error) {
      showNotification(`❌ Error updating contact`, 'error');
    } finally {
      setLoadingData(false);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateRemarks = async (contactId, text) => {
    try {
      if (!text || !text.trim()) return;
      await api.post(`/contacts/remark/${contactId}`, { text });
      showNotification('✅ Remarks updated!', 'success');
      fetchContacts();
      fetchStats();
    } catch (error) {
      showNotification('❌ Error updating remarks', 'error');
    }
  };

  const handleUserFormChange = (e) => {
    const { name, value } = e.target;
    setUserFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenAddUser = () => {
    setUserFormData({ name: '', email: '', phone: '', password: '', role: '' });
    setShowAddUserModal(true);
  };

  const handleOpenEditUser = (userItem) => {
    setEditingUser(userItem);
    setUserFormData({
      name: userItem.name || '',
      email: userItem.email || '',
      phone: userItem.phone || '',
      password: '',
      role: userItem.role?._id || userItem.role || ''
    });
    setShowEditUserModal(true);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      setLoadingData(true);
      await api.post('/create-employee', userFormData);
      showNotification('✅ User created successfully!', 'success');
      setShowAddUserModal(false);
      await fetchUsers();
      fetchStats();
    } catch (error) {
      showNotification(`❌ ${error.response?.data?.message || 'Error creating user'}`, 'error');
    } finally {
      setLoadingData(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser?._id) return;
    try {
      setLoadingData(true);
      const payload = { name: userFormData.name, email: userFormData.email, phone: userFormData.phone };
      if (userFormData.role) payload.role = userFormData.role;
      await api.put(`/user/${editingUser._id}`, payload);
      showNotification('✅ User updated successfully!', 'success');
      setShowEditUserModal(false);
      setEditingUser(null);
      await fetchUsers();
    } catch (error) {
      showNotification(`❌ ${error.response?.data?.message || 'Error updating user'}`, 'error');
    } finally {
      setLoadingData(false);
    }
  };

  const handleAssignContact = async (e) => {
    e.preventDefault();
    if (!assigningContactId || !assignContactUserId) return;
    try {
      setLoadingData(true);
      await api.post('/contacts/assign', { contactId: assigningContactId, userId: assignContactUserId });
      showNotification('✅ Contact assigned successfully!', 'success');
      setShowAssignContactModal(false);
      setAssigningContactId('');
      setAssignContactUserId('');
      fetchContacts();
    } catch (error) {
      showNotification(`❌ ${error.response?.data?.message || 'Error assigning contact'}`, 'error');
    } finally {
      setLoadingData(false);
    }
  };

  const handleUpdateMyContactStatus = async (contactId, status) => {
    try {
      await api.put(`/contacts/status/${contactId}`, { status });
      showNotification('✅ Status updated!', 'success');
      fetchMyContacts();
    } catch (error) {
      showNotification(`❌ ${error.response?.data?.message || 'Error updating status'}`, 'error');
    }
  };

  const handleAddMyRemark = async (contactId, text) => {
    if (!text || !text.trim()) return;
    try {
      await api.post(`/contacts/remark/${contactId}`, { text });
      showNotification('✅ Note added!', 'success');
      fetchMyContacts();
    } catch (error) {
      showNotification(`❌ ${error.response?.data?.message || 'Error adding note'}`, 'error');
    }
  };

  // Add note from inside the contact detail modal (works for all roles)
  const handleAddModalNote = async (contactId, text) => {
    if (!text || !text.trim()) return;
    try {
      await api.post(`/contacts/remark/${contactId}`, { text });
      showNotification('✅ Note added!', 'success');
      // Refresh modal with full detail (history + populated remarks)
      const res = await api.get(`/contacts/${contactId}`);
      if (res.data?.data) setSelectedItem(res.data.data);
      // Also refresh the list in the background
      if (isPrivileged) fetchContacts();
      else fetchMyContacts();
    } catch (error) {
      showNotification(`❌ ${error.response?.data?.message || 'Error adding note'}`, 'error');
    }
  };

  // Open contact detail modal with full history/remarks populated
  const handleViewContact = async (contact) => {
    setSelectedItem(contact); // show modal immediately with list data
    setShowContactModal(true);
    try {
      const res = await api.get(`/contacts/${contact._id}`);
      if (res.data?.data) setSelectedItem(res.data.data);
    } catch {}
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim()) return;
    try {
      setLoadingData(true);
      await api.post('/roles', { name: newRoleName.trim() });
      showNotification('✅ Role created successfully!', 'success');
      setShowAddRoleModal(false);
      setNewRoleName('');
      fetchRoles();
    } catch (error) {
      showNotification(`❌ ${error.response?.data?.message || 'Error creating role'}`, 'error');
    } finally {
      setLoadingData(false);
    }
  };

  const handleDeleteRole = async (roleId, roleName) => {
    if (window.confirm(`Delete role "${roleName}"? Users with this role must be reassigned first.`)) {
      try {
        setLoadingData(true);
        await api.delete(`/roles/${roleId}`);
        showNotification(`✅ Role "${roleName}" deleted`, 'success');
        fetchRoles();
      } catch (error) {
        showNotification(`❌ ${error.response?.data?.message || 'Error deleting role'}`, 'error');
      } finally {
        setLoadingData(false);
      }
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!isSuperAdmin) {
      showNotification('❌ Access denied', 'error');
      return;
    }
    if (user?.email === userEmail) {
      showNotification('❌ Cannot delete your own account', 'error');
      return;
    }
    if (window.confirm(`Delete user "${userEmail}"?`)) {
      try {
        setLoadingData(true);
        await api.delete(`/user/${userId}`);
        showNotification(`✅ User deleted`, 'success');
        await fetchUsers();
        fetchStats();
      } catch (error) {
        showNotification(`❌ Error deleting user`, 'error');
      } finally {
        setLoadingData(false);
      }
    }
  };

  const handleDeleteContact = async (contactId, contactName) => {
    if (!isSuperAdmin) {
      showNotification('❌ Access denied', 'error');
      return;
    }
    if (window.confirm(`Delete contact from "${contactName}"?`)) {
      try {
        setLoadingData(true);
        await api.delete(`/contacts/${contactId}`);
        showNotification(`✅ Contact deleted`, 'success');
        await fetchContacts();
        fetchStats();
      } catch (error) {
        showNotification(`❌ Error deleting contact`, 'error');
      } finally {
        setLoadingData(false);
      }
    }
  };

  const handleUpdateUserRole = async (userId, roleId) => {
    if (!isSuperAdmin) {
      showNotification('❌ Access denied', 'error');
      return;
    }
    try {
      setLoadingData(true);
      await api.post('/assign-role', { userId, roleId });
      showNotification(`✅ Role assigned`, 'success');
      await fetchUsers();
      setShowAssignRoleModal(false);
      setSelectedUserId('');
      setSelectedUserEmail('');
      setSelectedRoleId('');
    } catch (error) {
      showNotification('❌ Error assigning role', 'error');
    } finally {
      setLoadingData(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  // ===== ATTENDANCE FUNCTIONS =====

  const fetchTodayAttendance = async () => {
    try {
      const res = await api.get('/attendance/today');
      setTodayAttendance(res.data.data || null);
    } catch {
      setTodayAttendance(null);
    }
  };

  const fetchMyAttendanceStats = async () => {
    const now = new Date();
    try {
      const res = await api.get(`/attendance/my/stats?year=${now.getFullYear()}&month=${now.getMonth() + 1}`);
      setMyAttendanceStats(res.data.data || {});
    } catch { /* silent */ }
  };

  const fetchMyAttendanceHistory = async (page = 1) => {
    try {
      setAttendanceLoading(true);
      const res = await api.get(`/attendance/my?page=${page}&limit=10`);
      setMyAttendance(res.data.data || []);
      setMyAttendanceTotalPages(res.data.totalPages || 1);
      setMyAttendancePage(page);
    } catch { /* silent */ }
    finally { setAttendanceLoading(false); }
  };

  const fetchAllAttendance = async (page = 1, dateFilter = attendanceDateFilter, userFilter = attendanceUserFilter) => {
    try {
      setAttendanceLoading(true);
      let url = `/attendance?page=${page}&limit=20`;
      if (dateFilter) url += `&date=${dateFilter}`;
      if (userFilter) url += `&userId=${userFilter}`;
      const res = await api.get(url);
      setAllAttendance(res.data.data || []);
      setAllAttendanceTotalPages(res.data.totalPages || 1);
      setAllAttendancePage(page);
    } catch { /* silent */ }
    finally { setAttendanceLoading(false); }
  };

  const handleCheckIn = async () => {
    try {
      setCheckInOutLoading(true);
      await api.post('/attendance/checkin');
      await fetchTodayAttendance();
      await fetchMyAttendanceStats();
      showNotification('✅ Checked in successfully!', 'success');
    } catch (err) {
      showNotification(err.response?.data?.message || 'Check-in failed', 'error');
    } finally {
      setCheckInOutLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setShowCheckoutConfirm(false);
    try {
      setCheckInOutLoading(true);
      await api.post('/attendance/checkout');
      await fetchTodayAttendance();
      await fetchMyAttendanceStats();
      showNotification('✅ Checked out successfully!', 'success');
    } catch (err) {
      showNotification(err.response?.data?.message || 'Check-out failed', 'error');
    } finally {
      setCheckInOutLoading(false);
    }
  };

  const handleLunchIn = async () => {
    try {
      setLunchLoading(true);
      await api.post('/attendance/lunch-in');
      await fetchTodayAttendance();
      showNotification('🍽️ Lunch break started!', 'success');
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed', 'error');
    } finally {
      setLunchLoading(false);
    }
  };

  const handleLunchOut = async () => {
    try {
      setLunchLoading(true);
      await api.post('/attendance/lunch-out');
      await fetchTodayAttendance();
      showNotification('✅ Welcome back! Lunch break ended.', 'success');
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed', 'error');
    } finally {
      setLunchLoading(false);
    }
  };

  const handleShortBreakIn = async () => {
    try {
      setShortBreakLoading(true);
      await api.post('/attendance/short-break-in');
      await fetchTodayAttendance();
      showNotification('⏸️ Short break started. See you soon!', 'success');
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed', 'error');
    } finally {
      setShortBreakLoading(false);
    }
  };

  const handleShortBreakOut = async () => {
    try {
      setShortBreakLoading(true);
      await api.post('/attendance/short-break-out');
      await fetchTodayAttendance();
      showNotification('✅ Back to work!', 'success');
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed', 'error');
    } finally {
      setShortBreakLoading(false);
    }
  };

  const handleManualMark = async () => {
    try {
      if (!manualMarkForm.userId || !manualMarkForm.date || !manualMarkForm.status) {
        showNotification('All fields are required', 'error'); return;
      }
      await api.post('/attendance/manual', manualMarkForm);
      setShowManualMarkModal(false);
      setManualMarkForm({ userId: '', date: '', status: 'absent', notes: '' });
      showNotification('✅ Attendance marked successfully!', 'success');
      fetchAllAttendance(allAttendancePage);
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to mark attendance', 'error');
    }
  };

  const formatTime = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatDateShort = (dt) => {
    if (!dt) return '—';
    return new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Persistent bell notifications (socket events — don't auto-dismiss)
  const NOTIF_ICONS = { new_contact: '🆕', contact_assigned: '👤', status_changed: '📊', note_added: '📝' };

  const addNotification = (type, message, data = {}) => {
    setNotifications(prev => [
      { id: Date.now(), type, message, data, time: new Date(), read: false },
      ...prev
    ].slice(0, 50));
  };

  const markNotifRead = (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllNotifRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const formatNotifTime = (date) => {
    const diff = Math.floor((Date.now() - new Date(date)) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(date).toLocaleDateString();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const getInitials = () => {
    const name = user?.name || user?.email || 'A';
    return name.charAt(0).toUpperCase();
  };

  const handleNavClick = () => {
    if (windowWidth <= 768) setMobileMenuOpen(false);
  };

  const handleRefresh = () => {
    if (!isPrivileged) {
      fetchMyContacts();
    } else if (activeTab === 'contacts' || activeTab === 'dashboard') {
      fetchContacts();
      fetchStats();
    } else if (activeTab === 'users' || activeTab === 'roles') {
      fetchUsers();
      fetchStats();
    } else {
      fetchStats();
    }
    showNotification('Data refreshed!', 'success');
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  // ===== ATTENDANCE TAB =====
  const renderAttendanceTab = () => {
    const STATUS_COLORS = {
      present: '#16a34a', absent: '#dc2626', half_day: '#d97706',
      late: '#9333ea', leave: '#2563eb'
    };
    const STATUS_LABELS = {
      present: 'Present', absent: 'Absent', half_day: 'Half Day',
      late: 'Late', leave: 'On Leave'
    };

    // Today card
    const renderTodayCard = () => {
      const checkedIn       = !!todayAttendance?.checkIn;
      const checkedOut      = !!todayAttendance?.checkOut;
      const onLunch         = !!todayAttendance?.lunchBreakIn && !todayAttendance?.lunchBreakOut;
      const lunchDone       = !!todayAttendance?.lunchBreakIn && !!todayAttendance?.lunchBreakOut;
      const shortBreaks     = todayAttendance?.shortBreaks || [];
      const onShortBreak    = shortBreaks.some(b => !b.breakOut);
      const totalShortMins  = todayAttendance?.totalShortBreakMinutes || 0;
      const anyBreakActive  = onLunch || onShortBreak;

      return (
        <div className="attendance-today-card">
          <div className="attendance-today-header">
            <h3>🕐 Today's Attendance</h3>
            <span className="attendance-date">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</span>
          </div>
          <div className="attendance-today-body">
            {todayAttendance === undefined ? (
              <p className="attendance-loading">Loading...</p>
            ) : checkedIn ? (
              <div className="attendance-times">
                <div className="attendance-time-item">
                  <span className="att-time-label">Check In</span>
                  <span className="att-time-value checkin">{formatTime(todayAttendance.checkIn)}</span>
                </div>
                {checkedOut && <>
                  <div className="attendance-time-divider">→</div>
                  <div className="attendance-time-item">
                    <span className="att-time-label">Check Out</span>
                    <span className="att-time-value checkout">{formatTime(todayAttendance.checkOut)}</span>
                  </div>
                  <div className="attendance-time-item">
                    <span className="att-time-label">Work Hours</span>
                    <span className="att-time-value hours">{todayAttendance.workHours}h</span>
                  </div>
                </>}
                {lunchDone && (
                  <div className="attendance-time-item">
                    <span className="att-time-label">🍽️ Lunch</span>
                    <span className="att-time-value" style={{ color: '#f59e0b', fontSize: '1rem' }}>
                      {todayAttendance.lunchBreakMinutes} min
                    </span>
                  </div>
                )}
                {totalShortMins > 0 && (
                  <div className="attendance-time-item">
                    <span className="att-time-label">☕ Short Breaks</span>
                    <span className="att-time-value" style={{ color: '#8b5cf6', fontSize: '1rem' }}>
                      {totalShortMins} min ({shortBreaks.filter(b => b.breakOut).length}x)
                    </span>
                  </div>
                )}
                {onLunch && (
                  <div className="att-on-lunch-badge">🍽️ On Lunch Break since {formatTime(todayAttendance.lunchBreakIn)}</div>
                )}
                {onShortBreak && (
                  <div className="att-on-short-break-badge">☕ On Short Break since {formatTime(shortBreaks.find(b => !b.breakOut)?.breakIn)}</div>
                )}
              </div>
            ) : (
              <p className="attendance-not-in">You haven't checked in yet today</p>
            )}

            <div className="attendance-action-btns">
              {todayAttendance === undefined ? null : !checkedIn ? (
                <button className="att-checkin-btn" onClick={handleCheckIn} disabled={checkInOutLoading}>
                  {checkInOutLoading ? 'Processing...' : '✅ Check In'}
                </button>
              ) : checkedOut ? (
                <span className="att-done-badge">✓ Work complete for today</span>
              ) : (
                <div className="att-working-btns">
                  {/* Lunch break button — show only if lunch not taken yet */}
                  {!onLunch && !lunchDone && !onShortBreak && (
                    <button className="att-lunch-in-btn" onClick={handleLunchIn} disabled={lunchLoading}>
                      {lunchLoading ? '...' : '🍽️ Lunch Break'}
                    </button>
                  )}
                  {onLunch && (
                    <button className="att-lunch-out-btn" onClick={handleLunchOut} disabled={lunchLoading}>
                      {lunchLoading ? '...' : '▶️ Resume (Lunch)'}
                    </button>
                  )}
                  {/* Short break button — available anytime while working */}
                  {!onShortBreak && !onLunch && (
                    <button className="att-short-break-btn" onClick={handleShortBreakIn} disabled={shortBreakLoading}>
                      {shortBreakLoading ? '...' : '☕ Short Break'}
                    </button>
                  )}
                  {onShortBreak && (
                    <button className="att-short-break-out-btn" onClick={handleShortBreakOut} disabled={shortBreakLoading}>
                      {shortBreakLoading ? '...' : '▶️ Back to Work'}
                    </button>
                  )}
                  <button className="att-checkout-btn" onClick={() => setShowCheckoutConfirm(true)} disabled={checkInOutLoading || anyBreakActive} title={anyBreakActive ? 'End your break first' : ''}>
                    {checkInOutLoading ? 'Processing...' : '🏁 Check Out'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    // Monthly stats cards
    const renderMonthStats = () => (
      <div className="attendance-stats-row">
        {[
          { key: 'present',  label: 'Present',   icon: '✅', color: '#16a34a' },
          { key: 'absent',   label: 'Absent',    icon: '❌', color: '#dc2626' },
          { key: 'half_day', label: 'Half Day',  icon: '🌓', color: '#d97706' },
          { key: 'leave',    label: 'On Leave',  icon: '🏖️', color: '#2563eb' },
          { key: 'total',    label: 'This Month', icon: '📅', color: '#6366f1' },
        ].map(({ key, label, icon, color }) => (
          <div className="att-stat-card" key={key} style={{ borderTop: `3px solid ${color}` }}>
            <span className="att-stat-icon">{icon}</span>
            <span className="att-stat-value" style={{ color }}>{myAttendanceStats[key] ?? 0}</span>
            <span className="att-stat-label">{label}</span>
          </div>
        ))}
      </div>
    );

    // My attendance history table
    const renderMyHistory = () => (
      <div className="table-container" style={{ marginTop: '1.5rem' }}>
        <div className="table-header">
          <h3>📋 My Attendance History</h3>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Check In</th>
              <th>Check Out</th>
              <th>Work Hours</th>
              <th>🍽️ Lunch</th>
              <th>☕ Short Breaks</th>
              <th>Status</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {attendanceLoading ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading...</td></tr>
            ) : myAttendance.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>No attendance records yet</td></tr>
            ) : myAttendance.map((rec, i) => (
              <tr key={rec._id || i}>
                <td>{formatDateShort(rec.date)}</td>
                <td>{formatTime(rec.checkIn)}</td>
                <td>{formatTime(rec.checkOut)}</td>
                <td>{rec.workHours != null ? `${rec.workHours}h` : '—'}</td>
                <td>{rec.lunchBreakMinutes != null ? `${rec.lunchBreakMinutes} min` : '—'}</td>
                <td>{rec.totalShortBreakMinutes ? `${rec.totalShortBreakMinutes} min (${(rec.shortBreaks || []).filter(b => b.breakOut).length}x)` : '—'}</td>
                <td>
                  <span className="att-status-badge" style={{ background: STATUS_COLORS[rec.status] + '20', color: STATUS_COLORS[rec.status] }}>
                    {STATUS_LABELS[rec.status] || rec.status}
                  </span>
                </td>
                <td>{rec.notes || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {myAttendanceTotalPages > 1 && (
          <div className="pagination">
            <button onClick={() => fetchMyAttendanceHistory(myAttendancePage - 1)} disabled={myAttendancePage === 1}>← Previous</button>
            <span>Page {myAttendancePage} of {myAttendanceTotalPages}</span>
            <button onClick={() => fetchMyAttendanceHistory(myAttendancePage + 1)} disabled={myAttendancePage === myAttendanceTotalPages}>Next →</button>
          </div>
        )}
      </div>
    );

    // All employees table (HR/Admin only)
    const renderAllEmployeesAttendance = () => {
      if (!isPrivileged) return null;
      return (
        <div className="table-container" style={{ marginTop: '1.5rem' }}>
          <div className="table-header">
            <h3>👥 All Employees Attendance</h3>
            <div className="table-actions">
              <input
                type="date"
                value={attendanceDateFilter}
                onChange={e => { setAttendanceDateFilter(e.target.value); fetchAllAttendance(1, e.target.value, attendanceUserFilter); }}
                className="search-input"
                style={{ width: '150px' }}
              />
              <select
                value={attendanceUserFilter}
                onChange={e => { setAttendanceUserFilter(e.target.value); fetchAllAttendance(1, attendanceDateFilter, e.target.value); }}
                className="per-page-select"
                style={{ minWidth: '140px', padding: '0 8px', appearance: 'auto' }}
              >
                <option value="">All Employees</option>
                {users.map(u => <option key={u._id} value={u._id}>{u.name || u.email}</option>)}
              </select>
              {(attendanceDateFilter || attendanceUserFilter) && (
                <button className="clear-filter-btn" onClick={() => { setAttendanceDateFilter(''); setAttendanceUserFilter(''); fetchAllAttendance(1, '', ''); }}>✕ Clear</button>
              )}
              <button className="att-manual-btn" onClick={() => setShowManualMarkModal(true)}>✏️ Manual Mark</button>
              <button onClick={() => fetchAllAttendance(allAttendancePage)} className="refresh-btn">🔄</button>
            </div>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Role</th>
                <th>Date</th>
                <th>Check In</th>
                <th>Check Out</th>
                <th>Work Hours</th>
                <th>🍽️ Lunch</th>
                <th>☕ Short Breaks</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {attendanceLoading ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading...</td></tr>
              ) : allAttendance.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>No records found</td></tr>
              ) : allAttendance.map((rec, i) => (
                <tr key={rec._id || i}>
                  <td>{rec.userId?.name || '—'}</td>
                  <td><span style={{ fontSize: '0.75rem', color: '#6366f1', background: '#eef2ff', padding: '2px 7px', borderRadius: '10px' }}>{rec.userId?.role?.name || rec.userId?.role || '—'}</span></td>
                  <td>{formatDateShort(rec.date)}</td>
                  <td>{formatTime(rec.checkIn)}</td>
                  <td>{formatTime(rec.checkOut)}</td>
                  <td>{rec.workHours != null ? `${rec.workHours}h` : '—'}</td>
                  <td>{rec.lunchBreakMinutes != null ? `${rec.lunchBreakMinutes} min` : '—'}</td>
                  <td>{rec.totalShortBreakMinutes ? `${rec.totalShortBreakMinutes} min (${(rec.shortBreaks || []).filter(b => b.breakOut).length}x)` : '—'}</td>
                  <td>
                    <span className="att-status-badge" style={{ background: STATUS_COLORS[rec.status] + '20', color: STATUS_COLORS[rec.status] }}>
                      {STATUS_LABELS[rec.status] || rec.status}
                    </span>
                  </td>
                  <td>{rec.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {allAttendanceTotalPages > 1 && (
            <div className="pagination">
              <button onClick={() => fetchAllAttendance(allAttendancePage - 1)} disabled={allAttendancePage === 1}>← Previous</button>
              <span>Page {allAttendancePage} of {allAttendanceTotalPages}</span>
              <button onClick={() => fetchAllAttendance(allAttendancePage + 1)} disabled={allAttendancePage === allAttendanceTotalPages}>Next →</button>
            </div>
          )}
        </div>
      );
    };

    return (
      <>
        {renderTodayCard()}
        {renderMonthStats()}
        {renderMyHistory()}
        {renderAllEmployeesAttendance()}

        {/* Checkout Confirmation Modal */}
        {showCheckoutConfirm && (
          <div className="modal-overlay" onClick={() => setShowCheckoutConfirm(false)}>
            <div className="modal-content" style={{ maxWidth: '380px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🏁</div>
              <h2 style={{ marginBottom: '0.5rem', fontSize: '1.1rem' }}>Confirm Check Out</h2>
              <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                Are you sure you want to clock out?<br />
                <strong>After checkout you cannot clock in again today.</strong>
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                <button className="btn-secondary" onClick={() => setShowCheckoutConfirm(false)}>Cancel</button>
                <button className="att-checkout-btn" style={{ padding: '0.5rem 1.5rem' }} onClick={handleCheckOut}>Yes, Check Out</button>
              </div>
            </div>
          </div>
        )}

        {/* Manual Mark Modal */}
        {showManualMarkModal && (
          <div className="modal-overlay" onClick={() => setShowManualMarkModal(false)}>
            <div className="modal-content" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>✏️ Manual Attendance Mark</h2>
                <button className="modal-close" onClick={() => setShowManualMarkModal(false)}>✕</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Employee</label>
                  <select value={manualMarkForm.userId} onChange={e => setManualMarkForm(p => ({ ...p, userId: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid #d1d5db' }}>
                    <option value="">Select employee</option>
                    {users.map(u => <option key={u._id} value={u._id}>{u.name || u.email} ({u.role})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={manualMarkForm.date} onChange={e => setManualMarkForm(p => ({ ...p, date: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid #d1d5db' }} />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={manualMarkForm.status} onChange={e => setManualMarkForm(p => ({ ...p, status: e.target.value }))} style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid #d1d5db' }}>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="half_day">Half Day</option>
                    <option value="late">Late</option>
                    <option value="leave">On Leave</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Notes (optional)</label>
                  <input type="text" value={manualMarkForm.notes} onChange={e => setManualMarkForm(p => ({ ...p, notes: e.target.value }))} placeholder="e.g. Sick leave" style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid #d1d5db' }} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowManualMarkModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleManualMark}>Mark Attendance</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // RENDER FUNCTIONS
  const renderContent = () => {
    if (activeTab === 'attendance') return renderAttendanceTab();

    if (!isPrivileged) {
      return activeTab === 'dashboard' ? renderMyDashboard() : renderMyContactsTable();
    }

    if (isHR) {
      if (activeTab === 'dashboard') return renderHRDashboard();
      if (activeTab === 'users') return renderUsersTable();
      return renderContactsTable();
    }

    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'users': return renderUsersTable();
      case 'contacts': return (
        <>
          {renderStatsCards()}
          {renderContactsTable()}
        </>
      );
      case 'roles': return renderRolesTable();
      default: return renderDashboard();
    }
  };

  const renderHRDashboard = () => {
    return (
      <>
        {renderStatsCards()}
        
        <div className="csv-export-section">
          <h3>📥 Export Data</h3>
          <div className="export-buttons">
            <button onClick={exportContactsToCSV} className="export-csv-btn" disabled={exportingCSV}>
              <span>💬</span> Export Contacts CSV
            </button>
          </div>
        </div>

        <div className="recent-activity">
          <h3>📊 Recent Contacts</h3>
          <div className="activity-list">
            {contacts.slice(0, 10).map((contact, index) => (
              <div key={contact._id} className="activity-item">
                <div className="activity-icon">💬</div>
                <div className="activity-details">
                  <p><strong>{index + 1}. {contact.name}</strong> - {contact.email}</p>
                  <small>Phone: {contact.phone || 'N/A'} | Service: {contact.service || 'N/A'}</small>
                  <br />
                  <small>Date: {contact.createdAt ? new Date(contact.createdAt).toLocaleString() : 'N/A'}</small>
                </div>
                {getLatestRemark(contact.remarks) && <span className="remarks-badge">{getLatestRemark(contact.remarks).substring(0, 30)}...</span>}
              </div>
            ))}
            {contacts.length === 0 && <p className="no-activity">No contacts found</p>}
          </div>
          
          {contacts.length > 0 && (
            <div className="view-all-container">
              <button onClick={() => setActiveTab('contacts')} className="view-all-btn">
                View All Contacts →
              </button>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderMyDashboard = () => {
    return (
      <>
        <div className="dashboard-stats">
          <div className="stats-grid two-cols" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <div className="stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-details"><h3>My Contacts</h3><p className="stat-number">{myContactStats.total || 0}</p><span className="stat-badge">Total assigned</span></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🏆</div>
              <div className="stat-details"><h3>Won</h3><p className="stat-number">{myContactStats.won || 0}</p><span className="stat-badge">Closed won</span></div>
            </div>
          </div>
          {renderStatusCards(
            myContactStats,
            myStatusFilter,
            (s) => { setMyCurrentPage(1); setMyStatusFilter(s); if (s) setActiveTab('mycontacts'); }
          )}
        </div>
        <div className="recent-activity">
          <h3>📋 My Recent Contacts</h3>
          <div className="activity-list">
            {myContacts.slice(0, 5).map((contact, i) => (
              <div key={contact._id} className="activity-item">
                <div className="activity-icon">💬</div>
                <div className="activity-details">
                  <p><strong>{i + 1}. {contact.name}</strong> — {contact.phone}</p>
                  <small>{contact.service || 'N/A'} | <span className={`status-badge ${contact.status}`}>{contact.status}</span></small>
                </div>
              </div>
            ))}
            {myContacts.length === 0 && <p className="no-activity">No contacts assigned yet</p>}
          </div>
          {myContacts.length > 0 && (
            <div className="view-all-container">
              <button onClick={() => setActiveTab('mycontacts')} className="view-all-btn">View All My Contacts →</button>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderMyContactsTable = () => {
    const statusOptions = ['in_progress', 'followup', 'won', 'lost'];
    return (
      <div className="table-container">
        <div className="table-header">
          <h3>
            📋 My Assigned Contacts
            {myStatusFilter && (
              <span className={`status-badge ${myStatusFilter}`} style={{ marginLeft: 10, fontSize: '0.75rem' }}>
                {STATUS_CONFIG[myStatusFilter]?.label}
              </span>
            )}
          </h3>
          <div className="table-actions">
            {myStatusFilter && (
              <button onClick={() => { setMyCurrentPage(1); setMyStatusFilter(''); }} className="clear-filter-btn">✕ Clear Filter</button>
            )}
            <div className="per-page-wrapper">
              <select
                value={myItemsPerPage}
                onChange={e => { setMyItemsPerPage(Number(e.target.value)); setMyCurrentPage(1); }}
                className="per-page-select"
              >
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <button onClick={() => fetchMyContacts()} className="refresh-btn">🔄</button>
          </div>
        </div>
        <table className="data-table">
          <colgroup>
            <col style={{width:'52px'}} />
            <col style={{width:'150px'}} />
            <col style={{width:'110px'}} />
            <col style={{width:'110px'}} />
            <col style={{width:'115px'}} />
            <col style={{width:'160px'}} />
            <col style={{minWidth:'160px'}} />
            <col style={{width:'64px'}} />
          </colgroup>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Service</th>
              <th>Status</th>
              <th>Latest Note</th>
              <th>Add Note</th>
              <th>View</th>
            </tr>
          </thead>
          <tbody>
            {myContactsLoading ? (
              <tr><td colSpan="8" className="loading-cell">Loading...</td></tr>
            ) : myContacts.length === 0 ? (
              <tr><td colSpan="8" className="no-data">{myStatusFilter ? `No contacts with status "${STATUS_CONFIG[myStatusFilter]?.label}"` : 'No contacts assigned to you'}</td></tr>
            ) : (
              myContacts.map((contact, index) => (
                <tr key={contact._id}>
                  <td>{(myCurrentPage - 1) * myItemsPerPage + index + 1}</td>
                  <td>
                    <strong>{contact.name}</strong>
                    <br /><small>{contact.email}</small>
                  </td>
                  <td>{contact.phone}</td>
                  <td>{contact.service || 'N/A'}</td>
                  <td>
                    <select
                      value={contact.status}
                      onChange={(e) => {
                        if (e.target.value !== contact.status) {
                          handleUpdateMyContactStatus(contact._id, e.target.value);
                        }
                      }}
                      className={`status-badge ${contact.status}`}
                      style={{ border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem' }}
                    >
                      {(contact.status === 'new' || contact.status === 'assigned') && (
                        <option value={contact.status} disabled>{contact.status}</option>
                      )}
                      {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>{getLatestRemark(contact.remarks) || <span style={{color:'#aaa'}}>—</span>}</td>
                  <td>
                    <input
                      type="text"
                      placeholder="Type note + Enter"
                      className="remarks-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          handleAddMyRemark(contact._id, e.target.value.trim());
                          e.target.value = '';
                        }
                      }}
                    />
                  </td>
                  <td>
                    <button
                      className="action-btn view"
                      title="View History"
                      onClick={() => handleViewContact(contact)}
                    >📜</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {myTotalPages > 1 && (
          <div className="pagination">
            <button onClick={() => setMyCurrentPage(p => Math.max(1, p - 1))} disabled={myCurrentPage === 1} className="page-btn">← Previous</button>
            <span className="page-info">Page {myCurrentPage} of {myTotalPages}</span>
            <button onClick={() => setMyCurrentPage(p => Math.min(myTotalPages, p + 1))} disabled={myCurrentPage === myTotalPages} className="page-btn">Next →</button>
          </div>
        )}
      </div>
    );
  };

  const STATUS_CONFIG = {
    new:         { icon: '🆕', label: 'New',         color: '#6b7280' },
    assigned:    { icon: '👤', label: 'Assigned',    color: '#3b82f6' },
    in_progress: { icon: '⚡', label: 'In Progress', color: '#f59e0b' },
    followup:    { icon: '🔔', label: 'Follow Up',   color: '#8b5cf6' },
    won:         { icon: '🏆', label: 'Won',         color: '#22c55e' },
    lost:        { icon: '❌', label: 'Lost',        color: '#ef4444' },
  };

  const renderStatusCards = (counts, activeFilter, onFilterClick) => {
    const statuses = ['new', 'assigned', 'in_progress', 'followup', 'won', 'lost'];
    return (
      <div className="status-cards-grid">
        {statuses.map(status => {
          const count = counts[status] || 0;
          const cfg = STATUS_CONFIG[status];
          const isActive = activeFilter === status;
          return (
            <div
              key={status}
              className={`status-stat-card${isActive ? ' status-stat-card--active' : ''}`}
              style={{ borderLeft: `4px solid ${cfg.color}` }}
              onClick={() => onFilterClick(isActive ? '' : status)}
              title={`Click to filter by ${cfg.label}`}
            >
              <div className="status-stat-icon">{cfg.icon}</div>
              <div className="status-stat-details">
                <p className="status-stat-number" style={{ color: cfg.color }}>{count}</p>
                <h4>{cfg.label}</h4>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderStatsCards = () => {
    return (
      <div className="dashboard-stats">
        <div className={`stats-grid ${isSuperAdmin ? 'three-cols' : 'two-cols'}`}>
          {isSuperAdmin && (
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-details">
                <h3>Total Users</h3>
                <p className="stat-number">{stats.totalUsers}</p>
                <span className="stat-badge">Registered users</span>
              </div>
            </div>
          )}
          <div className="stat-card">
            <div className="stat-icon">💬</div>
            <div className="stat-details">
              <h3>Total Contacts</h3>
              <p className="stat-number">{stats.totalContacts}</p>
              <span className="stat-badge">All enquiries</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📝</div>
            <div className="stat-details">
              <h3>Unassigned</h3>
              <p className="stat-number">{contactStats.new || 0}</p>
              <span className="stat-badge">Needs action</span>
            </div>
          </div>
        </div>
        {renderStatusCards(
          contactStats,
          statusFilter,
          (s) => { setCurrentPage(1); setStatusFilter(s); if (s) setActiveTab('contacts'); }
        )}
      </div>
    );
  };

  const renderDashboard = () => {
    if (!isSuperAdmin) return null;
    return (
      <>
        {renderStatsCards()}
        <div className="csv-export-section">
          <h3>📥 Export Data</h3>
          <div className="export-buttons">
            <button onClick={exportUsersToCSV} className="export-csv-btn users-export" disabled={exportingCSV}>
              <span>👥</span> Export Users CSV
            </button>
            <button onClick={exportContactsToCSV} className="export-csv-btn contacts-export" disabled={exportingCSV}>
              <span>💬</span> Export Contacts CSV
            </button>
          </div>
        </div>
        <div className="recent-activity">
          <h3>📊 Recent Contacts</h3>
          <div className="activity-list">
            {contacts.slice(0, 5).map((contact, index) => (
              <div key={contact._id} className="activity-item">
                <div className="activity-icon">💬</div>
                <div className="activity-details">
                  <p><strong>{index + 1}. {contact.name}</strong> - {contact.email}</p>
                  <small>{contact.createdAt ? new Date(contact.createdAt).toLocaleString() : 'N/A'}</small>
                </div>
                {getLatestRemark(contact.remarks) && <span className="remarks-badge">{getLatestRemark(contact.remarks).substring(0, 20)}...</span>}
              </div>
            ))}
            {contacts.length === 0 && <p className="no-activity">No recent contacts</p>}
          </div>
        </div>
      </>
    );
  };

  const renderUsersTable = () => {
    if (!isSuperAdmin && !isHR) return null;
    return (
      <div className="table-container">
        <div className="table-header">
          <h3>👥 User Management</h3>
          <div className="table-actions">
            <button onClick={handleOpenAddUser} className="assign-role-btn">➕ Add User</button>
            {isSuperAdmin && (
              <button onClick={exportUsersToCSV} className="export-csv-btn small" disabled={exportingCSV}>
                📥 Export
              </button>
            )}
            <input type="text" placeholder="🔍 Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
            <button onClick={handleRefresh} className="refresh-btn">🔄</button>
          </div>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingData ? (
              <tr><td colSpan="7" className="loading-cell">Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan="7" className="no-data">No users found</td></tr>
            ) : (
              users.map((userItem, index) => {
                const itemRoleName = getRoleName(userItem.role).toLowerCase();
                const canEdit = isSuperAdmin || (isHR && itemRoleName !== 'superadmin');
                return (
                <tr key={userItem._id}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>{userItem.name || 'N/A'}</td>
                  <td>{userItem.email}</td>
                  <td>{userItem.phone || 'N/A'}</td>
                  <td><span className={`role-badge ${getRoleBadgeClass(userItem.role)}`}>{getRoleName(userItem.role)}</span></td>
                  <td><span className="status-badge active">Active</span></td>
                  <td className="action-buttons">
                    {canEdit && <button onClick={() => handleOpenEditUser(userItem)} className="action-btn edit" title="Edit User">✏️</button>}
                    {isSuperAdmin && (
                      <>
                        <button onClick={() => {
                          setSelectedUserId(userItem._id);
                          setSelectedUserEmail(userItem.email);
                          setSelectedRoleId(userItem.role?._id || userItem.role);
                          setShowAssignRoleModal(true);
                        }} className="action-btn role" title="Change Role">👑</button>
                        <button onClick={() => handleDeleteUser(userItem._id, userItem.email)} className="action-btn delete" title="Delete User">🗑️</button>
                      </>
                    )}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
        {totalPages > 1 && renderPagination()}
      </div>
    );
  };

  const renderContactsTable = () => {
    const displayContacts = filteredContacts;
    return (
      <div className="table-container">
        <div className="table-header">
          <h3>
            💬 Contact Messages
            {statusFilter && (
              <span className={`status-badge ${statusFilter}`} style={{ marginLeft: 10, fontSize: '0.75rem' }}>
                {STATUS_CONFIG[statusFilter]?.label}
              </span>
            )}
          </h3>
          <div className="table-actions">
            {statusFilter && (
              <button onClick={() => { setCurrentPage(1); setStatusFilter(''); }} className="clear-filter-btn">✕ Clear Filter</button>
            )}
            {isPrivileged && (
              <button onClick={exportContactsToCSV} className="export-csv-btn small" disabled={exportingCSV}>
                📥 Export
              </button>
            )}
            <input type="text" placeholder="🔍 Search name, email, phone..." value={searchTerm} onChange={(e) => { setCurrentPage(1); setSearchTerm(e.target.value); }} className="search-input" />
            <div className="per-page-wrapper">
              <select
                value={itemsPerPage}
                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="per-page-select"
              >
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <button onClick={handleRefresh} className="refresh-btn">🔄</button>
          </div>
        </div>
        <table className="data-table">
          <colgroup>
            <col style={{width:'52px'}} />
            <col style={{width:'140px'}} />
            <col style={{width:'155px'}} />
            <col style={{width:'110px'}} />
            <col style={{width:'110px'}} />
            <col style={{width:'115px'}} />
            <col style={{width:'120px'}} />
            <col style={{width:'160px'}} />
            <col style={{width:'95px'}} />
            <col style={{width:'120px'}} />
          </colgroup>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Service</th>
              <th>Status</th>
              <th>Assigned To</th>
              <th>Remarks</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingData ? (
              <tr><td colSpan="10" className="loading-cell">Loading...</td></tr>
            ) : displayContacts.length === 0 ? (
              <tr><td colSpan="10" className="no-data">{searchTerm ? 'No matching contacts found' : 'No contacts found'}</td></tr>
            ) : (
              displayContacts.map((contact, index) => (
                <tr key={contact._id}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td title={contact.name}>{contact.name || 'N/A'}</td>
                  <td title={contact.email}>{contact.email || 'N/A'}</td>
                  <td title={contact.phone}>{contact.phone || 'N/A'}</td>
                  <td title={contact.service || contact.businessType}>{contact.service || contact.businessType || 'N/A'}</td>
                  <td>
                    {isSuperAdmin ? (
                      <select
                        value={contact.status || 'new'}
                        onChange={(e) => {
                          api.put(`/contacts/status/${contact._id}`, { status: e.target.value })
                            .then(() => { fetchContacts(); fetchStats(); })
                            .catch((err) => showNotification(`❌ ${err.response?.data?.message || 'Error'}`, 'error'));
                        }}
                        className={`status-badge ${contact.status || 'new'}`}
                        style={{ border: 'none', cursor: 'pointer', fontWeight: 500 }}
                      >
                        {(contact.status === 'new' || contact.status === 'assigned') && (
                          <option value={contact.status} disabled>{contact.status}</option>
                        )}
                        <option value="in_progress">in_progress</option>
                        <option value="followup">followup</option>
                        <option value="won">won</option>
                        <option value="lost">lost</option>
                      </select>
                    ) : (
                      <span className={`status-badge ${contact.status || 'new'}`}>{contact.status || 'new'}</span>
                    )}
                  </td>
                  <td>{contact.assignedTo ? (contact.assignedTo.name || contact.assignedTo.email || 'Assigned') : <span style={{color:'#aaa'}}>Unassigned</span>}</td>
                  <td>
                    <input type="text" defaultValue={getLatestRemark(contact.remarks)} onBlur={(e) => handleUpdateRemarks(contact._id, e.target.value)} placeholder="Add remark..." className="remarks-input" />
                  </td>
                  <td>{contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : 'N/A'}</td>
                  <td className="action-buttons">
                    <button onClick={() => handleViewContact(contact)} className="action-btn view" title="View Details">👁️</button>
                    <button onClick={() => { setAssigningContactId(contact._id); setAssignContactUserId(''); setShowAssignContactModal(true); }} className="action-btn role" title="Assign to User">👤</button>
                    {isSuperAdmin && (
                      <>
                        <button onClick={() => handleEditContact(contact)} className="action-btn edit" title="Edit Contact">✏️</button>
                        <button onClick={() => handleDeleteContact(contact._id, contact.name)} className="action-btn delete" title="Delete Contact">🗑️</button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {totalPages > 1 && renderPagination()}
      </div>
    );
  };

  const renderRolesTable = () => {
    if (!isSuperAdmin) return null;
    return (
      <div className="table-container">
        <div className="table-header">
          <h3>👑 Roles Master</h3>
          <div className="table-actions">
            <button onClick={() => { setNewRoleName(''); setShowAddRoleModal(true); }} className="assign-role-btn">➕ Add Role</button>
            <button onClick={fetchRoles} className="refresh-btn">🔄 Refresh</button>
          </div>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Role Name</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingData ? (
              <tr><td colSpan="4" className="loading-cell">Loading...</td></tr>
            ) : roles.length === 0 ? (
              <tr><td colSpan="4" className="no-data">No roles found</td></tr>
            ) : (
              roles.map((role, index) => (
                <tr key={role._id}>
                  <td>{index + 1}</td>
                  <td><span className={`role-badge ${getRoleBadgeClass(role)}`}>{role.name}</span></td>
                  <td>{role.createdAt ? new Date(role.createdAt).toLocaleDateString() : 'N/A'}</td>
                  <td className="action-buttons">
                    <button
                      onClick={() => handleDeleteRole(role._id, role.name)}
                      className="action-btn delete"
                      title="Delete Role"
                      disabled={['superadmin', 'hr', 'user'].includes(role.name.toLowerCase())}
                    >🗑️</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <p style={{ padding: '8px 16px', fontSize: '12px', color: '#888' }}>
          * Built-in roles (superadmin, hr, user) cannot be deleted.
        </p>
      </div>
    );
  };

  const renderPagination = () => (
    <div className="pagination">
      <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="page-btn">← Previous</button>
      <span className="page-info">Page {currentPage} of {totalPages}</span>
      <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="page-btn">Next →</button>
    </div>
  );

  // MODALS
  const renderAssignRoleModal = () => (
    <div className="modal-overlay" onClick={() => setShowAssignRoleModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>👑 Assign Role</h2>
          <button className="close-btn" onClick={() => setShowAssignRoleModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Select User</label>
            <select value={selectedUserId} onChange={(e) => {
              const selected = users.find(u => u._id === e.target.value);
              setSelectedUserId(e.target.value);
              setSelectedUserEmail(selected?.email || '');
            }}>
              <option value="">-- Select User --</option>
              {users.map(u => <option key={u._id} value={u._id}>{u.email} {u.name ? `(${u.name})` : ''}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Select Role</label>
            <select value={selectedRoleId} onChange={(e) => setSelectedRoleId(e.target.value)}>
              <option value="">-- Select Role --</option>
              {roles.map(role => <option key={role._id} value={role._id}>👑 {role.name}</option>)}
            </select>
          </div>
          {selectedUserEmail && <div className="selected-user-info"><p>📧 Assigning to: <strong>{selectedUserEmail}</strong></p></div>}
        </div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={() => setShowAssignRoleModal(false)}>Cancel</button>
          <button className="save-btn" onClick={() => {
            if (!selectedUserId) { showNotification('Please select a user', 'error'); return; }
            if (!selectedRoleId) { showNotification('Please select a role', 'error'); return; }
            handleUpdateUserRole(selectedUserId, selectedRoleId);
          }} disabled={loadingData}>{loadingData ? 'Assigning...' : 'Assign Role'}</button>
        </div>
      </div>
    </div>
  );

  const renderContactModal = () => (
    <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📋 Contact Details</h2>
          <button className="close-btn" onClick={() => setShowContactModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="details-grid">
            <div className="detail-row"><strong>Name:</strong> <span>{selectedItem?.name || 'N/A'}</span></div>
            <div className="detail-row"><strong>Email:</strong> <span>{selectedItem?.email || 'N/A'}</span></div>
            <div className="detail-row"><strong>Phone:</strong> <span>{selectedItem?.phone || 'N/A'}</span></div>
            <div className="detail-row"><strong>Service:</strong> <span>{selectedItem?.service || selectedItem?.businessType || 'N/A'}</span></div>
            <div className="detail-row"><strong>Status:</strong> <span className={`status-badge ${selectedItem?.status}`}>{selectedItem?.status || 'new'}</span></div>
            {isPrivileged && (
              <>
                <div className="detail-row"><strong>Assigned To:</strong> <span>{selectedItem?.assignedTo?.name || selectedItem?.assignedTo?.email || 'Unassigned'}</span></div>
                <div className="detail-row"><strong>Assigned By:</strong> <span>{selectedItem?.assignedBy?.name || selectedItem?.assignedBy?.email || '—'}</span></div>
              </>
            )}
            <div className="detail-row full-width"><strong>Message:</strong> <p>{selectedItem?.message || selectedItem?.requirements || 'No message'}</p></div>
          </div>

          {/* Add Note — available for all logged-in roles */}
          <div style={{ marginTop: '16px' }}>
            <strong>📝 Notes {selectedItem?.remarks?.length > 0 ? `(${selectedItem.remarks.length})` : ''}</strong>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <input
                id="modal-note-input"
                type="text"
                placeholder="Type a note and press Add…"
                style={{ flex: 1, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    handleAddModalNote(selectedItem._id, e.target.value.trim());
                    e.target.value = '';
                  }
                }}
              />
              <button
                className="save-btn"
                style={{ padding: '7px 14px', fontSize: '13px' }}
                onClick={() => {
                  const inp = document.getElementById('modal-note-input');
                  if (inp?.value.trim()) {
                    handleAddModalNote(selectedItem._id, inp.value.trim());
                    inp.value = '';
                  }
                }}
              >Add</button>
            </div>
            {selectedItem?.remarks?.length > 0 && (
              <div style={{ marginTop: '10px', maxHeight: '160px', overflowY: 'auto' }}>
                {[...selectedItem.remarks].reverse().map((r, i) => (
                  <div key={i} style={{ padding: '6px 10px', background: '#f8f9fa', borderRadius: '6px', marginBottom: '5px', fontSize: '13px', borderLeft: '3px solid #6366f1' }}>
                    <span style={{ fontWeight: 500 }}>{r.text}</span>
                    <br /><small style={{ color: '#888' }}>— {r.createdBy?.name || r.createdBy?.email || 'Unknown'} &nbsp;·&nbsp; {new Date(r.createdAt).toLocaleString()}</small>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedItem?.history?.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <strong>📜 Audit History ({selectedItem.history.length})</strong>
              <div style={{ marginTop: '8px', maxHeight: '150px', overflowY: 'auto' }}>
                {[...selectedItem.history].reverse().map((h, i) => (
                  <div key={i} style={{ padding: '5px 8px', borderLeft: '3px solid #4f46e5', marginBottom: '6px', fontSize: '12px' }}>
                    <span>{h.details}</span>
                    <br /><small style={{ color: '#888' }}>by {h.performedBy?.name || h.performedBy?.email || 'Unknown'} · {new Date(h.createdAt).toLocaleString()}</small>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={() => setShowContactModal(false)}>Close</button>
        </div>
      </div>
    </div>
  );

  const renderEditContactModal = () => (
    <div className="modal-overlay" onClick={() => setShowEditContactModal(false)}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>✏️ Edit Contact</h2>
          <button className="close-btn" onClick={() => setShowEditContactModal(false)}>✕</button>
        </div>
        <form onSubmit={handleUpdateContact}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input type="text" name="name" value={editFormData.name} onChange={handleEditFormChange} required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={editFormData.email} onChange={handleEditFormChange} required />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="tel" name="phone" value={editFormData.phone} onChange={handleEditFormChange} />
              </div>
              <div className="form-group">
                <label>Service</label>
                <input type="text" name="service" value={editFormData.service} onChange={handleEditFormChange} />
              </div>
              <div className="form-group full-width">
                <label>Message</label>
                <textarea name="message" value={editFormData.message} onChange={handleEditFormChange} rows="4" />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={() => setShowEditContactModal(false)}>Cancel</button>
            <button type="submit" className="save-btn" disabled={loadingData}>{loadingData ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderAssignContactModal = () => (
    <div className="modal-overlay" onClick={() => setShowAssignContactModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>👤 Assign Contact</h2>
          <button className="close-btn" onClick={() => setShowAssignContactModal(false)}>✕</button>
        </div>
        <form onSubmit={handleAssignContact}>
          <div className="modal-body">
            <div className="form-group">
              <label>Assign To *</label>
              <select value={assignContactUserId} onChange={(e) => setAssignContactUserId(e.target.value)} required>
                <option value="">-- Select User --</option>
                {users.filter(u => {
                  const roleName = getRoleName(u.role).toLowerCase();
                  return roleName !== 'superadmin' && roleName !== 'hr';
                }).map(u => (
                  <option key={u._id} value={u._id}>
                    {u.name || u.email} ({getRoleName(u.role)})
                  </option>
                ))}
              </select>
              <small style={{ color: '#888', marginTop: '4px', display: 'block' }}>
                Only non-admin users shown (BDE, employees, etc.)
              </small>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={() => setShowAssignContactModal(false)}>Cancel</button>
            <button type="submit" className="save-btn" disabled={loadingData || !assignContactUserId}>
              {loadingData ? 'Assigning...' : 'Assign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderAddRoleModal = () => (
    <div className="modal-overlay" onClick={() => setShowAddRoleModal(false)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>👑 Add New Role</h2>
          <button className="close-btn" onClick={() => setShowAddRoleModal(false)}>✕</button>
        </div>
        <form onSubmit={handleAddRole}>
          <div className="modal-body">
            <div className="form-group">
              <label>Role Name *</label>
              <input
                type="text"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="e.g. bde, manager, intern"
                required
                autoFocus
              />
              <small style={{ color: '#888', marginTop: '4px', display: 'block' }}>
                Role name will be saved in lowercase. It will appear in all role dropdowns.
              </small>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={() => setShowAddRoleModal(false)}>Cancel</button>
            <button type="submit" className="save-btn" disabled={loadingData || !newRoleName.trim()}>
              {loadingData ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderAddUserModal = () => (
    <div className="modal-overlay" onClick={() => setShowAddUserModal(false)}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>➕ Add New User</h2>
          <button className="close-btn" onClick={() => setShowAddUserModal(false)}>✕</button>
        </div>
        <form onSubmit={handleAddUser}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input type="text" name="name" value={userFormData.name} onChange={handleUserFormChange} required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={userFormData.email} onChange={handleUserFormChange} required />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="tel" name="phone" value={userFormData.phone} onChange={handleUserFormChange} />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input type="password" name="password" value={userFormData.password} onChange={handleUserFormChange} required minLength={6} />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select name="role" value={userFormData.role} onChange={handleUserFormChange} required>
                  <option value="">-- Select Role --</option>
                  {roles
                    .filter(r => !isHR || r.name.toLowerCase() !== 'superadmin')
                    .map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={() => setShowAddUserModal(false)}>Cancel</button>
            <button type="submit" className="save-btn" disabled={loadingData}>{loadingData ? 'Creating...' : 'Create User'}</button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderEditUserModal = () => (
    <div className="modal-overlay" onClick={() => setShowEditUserModal(false)}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>✏️ Edit User</h2>
          <button className="close-btn" onClick={() => setShowEditUserModal(false)}>✕</button>
        </div>
        <form onSubmit={handleEditUser}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label>Name *</label>
                <input type="text" name="name" value={userFormData.name} onChange={handleUserFormChange} required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={userFormData.email} onChange={handleUserFormChange} required />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="tel" name="phone" value={userFormData.phone} onChange={handleUserFormChange} />
              </div>
              {isSuperAdmin && (
                <div className="form-group">
                  <label>Role</label>
                  <select name="role" value={userFormData.role} onChange={handleUserFormChange}>
                    <option value="">-- Keep Current --</option>
                    {roles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={() => setShowEditUserModal(false)}>Cancel</button>
            <button type="submit" className="save-btn" disabled={loadingData}>{loadingData ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="admin-dashboard">
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
        {mobileMenuOpen ? '✕' : '☰'}
      </button>
      {mobileMenuOpen && <div className="overlay" onClick={() => setMobileMenuOpen(false)} />}

      <div className={`dashboard-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <h3>{isSuperAdmin ? '⚡ Admin Panel' : isHR ? '📋 HR Panel' : '📋 My Panel'}</h3>
          </div>
          <div className="user-info">
            <div className="user-avatar">{getInitials()}</div>
            <div className="user-details">
              <p className="user-name">{user?.name || user?.email?.split('@')[0] || 'Admin'}</p>
              <p className="user-role">{getDisplayRoleName()}</p>
            </div>
          </div>
        </div>

        <div className="sidebar-nav">
          <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => { setActiveTab('dashboard'); handleNavClick(); }}>
            <span>📊</span><span>Dashboard</span>
          </button>
          {isPrivileged && (
            <button className={activeTab === 'users' ? 'active' : ''} onClick={() => { setActiveTab('users'); handleNavClick(); }}>
              <span>👥</span><span>Users</span>
            </button>
          )}
          {isSuperAdmin && (
            <button className={activeTab === 'roles' ? 'active' : ''} onClick={() => { setActiveTab('roles'); handleNavClick(); }}>
              <span>👑</span><span>Roles</span>
            </button>
          )}
          {isPrivileged ? (
            <button className={activeTab === 'contacts' ? 'active' : ''} onClick={() => { setActiveTab('contacts'); handleNavClick(); }}>
              <span>💬</span><span>All Contacts</span>
            </button>
          ) : (
            <button className={activeTab === 'mycontacts' ? 'active' : ''} onClick={() => { setActiveTab('mycontacts'); handleNavClick(); }}>
              <span>📋</span><span>My Contacts</span>
            </button>
          )}
          <button className={activeTab === 'attendance' ? 'active' : ''} onClick={() => { setActiveTab('attendance'); handleNavClick(); }}>
            <span>🕐</span><span>Attendance</span>
          </button>
        </div>

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-btn">
            <span>🚪</span><span>Logout</span>
          </button>
        </div>
      </div>

      <div className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-title">
            <h1>
              {activeTab === 'dashboard' && '📊 Dashboard'}
              {activeTab === 'users' && '👥 User Management'}
              {activeTab === 'roles' && '👑 Role Management'}
              {activeTab === 'contacts' && '💬 Contact Messages'}
              {activeTab === 'attendance' && '🕐 Attendance'}
              {activeTab === 'mycontacts' && '📋 My Contacts'}
            </h1>
            <p>Welcome back, {user?.name || user?.email?.split('@')[0] || 'Admin'}!</p>
          </div>
          <div className="header-actions">
            <div className="notif-bell-wrapper" ref={notifPanelRef}>
              <button
                className="notif-bell-btn"
                onClick={() => setShowNotifPanel(p => !p)}
                title="Notifications"
              >
                🔔
                {notifications.filter(n => !n.read).length > 0 && (
                  <span className="notif-badge">
                    {notifications.filter(n => !n.read).length > 99 ? '99+' : notifications.filter(n => !n.read).length}
                  </span>
                )}
              </button>

              {showNotifPanel && (
                <div className="notif-panel">
                  <div className="notif-panel-header">
                    <span>Notifications</span>
                    <div className="notif-panel-actions">
                      {notifications.some(n => !n.read) && (
                        <button className="notif-mark-all-btn" onClick={markAllNotifRead}>✓ Mark all read</button>
                      )}
                      {notifications.length > 0 && (
                        <button className="notif-clear-btn" onClick={() => setNotifications([])}>🗑</button>
                      )}
                    </div>
                  </div>
                  <div className="notif-list">
                    {notifications.length === 0 ? (
                      <p className="notif-empty">No notifications</p>
                    ) : (
                      notifications.map(n => (
                        <div key={n.id} className={`notif-item${n.read ? ' notif-item--read' : ''}`}>
                          <span className="notif-item-icon">{NOTIF_ICONS[n.type] || '🔔'}</span>
                          <div className="notif-item-body">
                            <p className="notif-item-msg">{n.message}</p>
                            <small className="notif-item-time">{formatNotifTime(n.time)}</small>
                          </div>
                          {!n.read && (
                            <button className="notif-read-btn" onClick={() => markNotifRead(n.id)} title="Mark as read">✓</button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <button onClick={handleRefresh} className="header-refresh-btn">🔄 Refresh</button>
            <button onClick={handleLogout} className="header-logout-btn">🚪 Logout</button>
          </div>
        </div>
        {renderContent()}
      </div>

      {showAssignRoleModal && renderAssignRoleModal()}
      {showContactModal && renderContactModal()}
      {showEditContactModal && renderEditContactModal()}
      {showAssignContactModal && renderAssignContactModal()}
      {showAddRoleModal && renderAddRoleModal()}
      {showAddUserModal && renderAddUserModal()}
      {showEditUserModal && renderEditUserModal()}
    </div>
  );
};

export default AdminDashboard;